<template>
  <div
    class="base-type-edit__toolbar"
    :class="{ 'base-type-edit__toolbar--sticky': sticky }"
  >
    <a
      v-if="admin2Url"
      class="btn btn-outline-primary btn-sm base-type-edit__admin2-link"
      :href="admin2Url"
      target="_blank"
      rel="noopener noreferrer"
    >
      <Icon icon="fa-pro-solid:sparkles" />
      Admin2
    </a>
    <a
      v-if="(!showPremiumPreview || showStandardPreviewWithPremium) && previewUrl"
      class="btn base-type-edit__preview-link"
      :href="previewUrl"
      target="_blank"
      rel="noopener noreferrer"
    >
      <Icon icon="fa-pro-solid:eye" />
      Vorschau
    </a>
    <a
      v-if="showPremiumPreview && premiumPreviewUrl"
      class="btn base-type-edit__preview-link base-type-edit__preview-link--premium"
      :href="premiumPreviewUrl"
      target="_blank"
      rel="noopener noreferrer"
    >
      <Icon icon="fa-pro-solid:sparkles" />
      Premium Vorschau
    </a>
    <div v-if="showValidationAlert" class="alert alert-warning mb-0" role="alert">
      {{ publicationMessage || 'Bitte überprüfen Sie Ihre Eingaben:' }}
      <ul class="mb-0 mt-2">
        <li v-for="(error, field) in validationErrors" :key="field">
          {{ error }}
        </li>
      </ul>
    </div>
    <div v-if="$slots.controls" class="base-type-edit__toolbar-controls">
      <slot name="controls" />
    </div>
    <div class="base-type-edit__toolbar-actions">
      <div class="base-type-edit__status dropup">
        <button
          v-if="statusActions.length"
          type="button"
          class="base-type-edit__status-badge dropdown-toggle"
          :class="statusClass"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          title="Sichtbarkeit ändern"
          :disabled="busy"
        >
          <span class="base-type-edit__status-dot" aria-hidden="true"></span>
          {{ statusLabel }}
        </button>
        <span v-else class="base-type-edit__status-badge" :class="statusClass">
          <span class="base-type-edit__status-dot" aria-hidden="true"></span>
          {{ statusLabel }}
        </span>
        <ul v-if="statusActions.length" class="dropdown-menu dropdown-menu-end">
          <li v-for="item in statusActions" :key="item.action">
            <button
              type="button"
              class="dropdown-item"
              :disabled="busy"
              @click="pendingDeactivation = item.action"
            >
              {{ item.label }}
            </button>
          </li>
        </ul>
      </div>
      <button
        type="submit"
        class="btn"
        :class="enabled ? 'btn-primary base-type-edit__toolbar-primary' : 'btn-outline-secondary'"
        :title="enabled
          ? 'Ihre Änderungen sind nach dem Speichern sofort öffentlich sichtbar.'
          : 'Speichert Ihre Eingaben, ohne sie zu veröffentlichen. Fehlende Angaben können Sie später ergänzen.'"
        :disabled="busy"
      >
        {{ enabled ? 'Speichern' : 'Entwurf speichern' }}
      </button>
      <button
        v-if="!enabled"
        type="button"
        class="btn btn-primary base-type-edit__toolbar-primary base-type-edit__toolbar-activate"
        :disabled="busy || activationDisabled"
        @click="$emit('publication-action', hasWebsite ? 'activate-website' : 'activate-marketing')"
      >
        {{ hasWebsite ? 'Speichern & veröffentlichen' : 'Speichern & auf mvp.de veröffentlichen' }}
      </button>
      <button
        v-else-if="hasWebsite && !marketed"
        type="button"
        class="btn base-type-edit__toolbar-secondary base-type-edit__toolbar-activate"
        :disabled="busy || activationDisabled"
        @click="$emit('update:marketingPrompt', 'confirm')"
      >
        Auch auf mvp.de zeigen
      </button>
    </div>

    <Dialog
      :visible="!!pendingDeactivation"
      modal
      class="publication-confirm"
      :header="deactivationText.header"
      :style="{width: '95vw', maxWidth: '520px'}"
      @update:visible="(visible) => { if (!visible) pendingDeactivation = null }"
    >
      <p>{{ deactivationText.body }}</p>
      <p class="mb-0 text-muted">
        Ihre Inhalte bleiben gespeichert. Sie können den Eintrag jederzeit wieder veröffentlichen.
      </p>
      <template #footer>
        <button type="button" class="btn btn-outline-secondary" @click="pendingDeactivation = null">
          Abbrechen
        </button>
        <button
          type="button"
          class="btn btn-danger publication-confirm__submit"
          :disabled="busy"
          @click="confirmDeactivation"
        >
          {{ deactivationText.confirm }}
        </button>
      </template>
    </Dialog>

    <MarketingPromptDialog
      :mode="hasWebsite ? marketingPrompt : null"
      :object-name="objectName"
      :package-base-type="packageBaseType"
      :busy="busy"
      @close="$emit('update:marketingPrompt', null)"
      @confirm="confirmMarketing"
    />
  </div>
</template>

<script>
import Dialog from 'primevue/dialog'
import MarketingPromptDialog from '@/components/base_type/MarketingPromptDialog.vue'
import {useUserStore} from "@/stores/user";
import {buildAdmin2ObjectLocalizationEditUrl} from "@/utils/admin2";
import { appendPremiumPreviewParam } from '@/utils/objectPreview'

export default {
  name: 'BaseTypeFormToolbar',
  components: {Dialog, MarketingPromptDialog},
  setup() {
    return {
      userStore: useUserStore(),
    }
  },
  emits: ['publication-action', 'update:marketingPrompt'],
  props: {
    enabled: {
      type: Boolean,
      default: false,
    },
    marketed: {
      type: Boolean,
      default: false,
    },
    hasWebsite: {
      type: Boolean,
      default: false,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    activationDisabled: {
      type: Boolean,
      default: false,
    },
    publicationErrors: {
      type: Object,
      default: () => ({}),
    },
    publicationMessage: {
      type: String,
      default: '',
    },
    errors: {
      type: Object,
      required: true,
    },
    objectName: {
      type: String,
      default: '',
    },
    // API base type (e.g. 'angebote'), used to show the matching mvp.de contingent.
    packageBaseType: {
      type: String,
      default: null,
    },
    // null, 'published' or 'confirm'; see MarketingPromptDialog.
    marketingPrompt: {
      type: String,
      default: null,
    },
    sticky: {
      type: Boolean,
      default: true,
    },
    previewUrl: {
      type: String,
      default: null,
    },
    showPremiumPreview: {
      type: Boolean,
      default: false,
    },
    showStandardPreviewWithPremium: {
      type: Boolean,
      default: false,
    },
    admin2ObjectId: {
      type: [Number, String],
      default: null,
    },
    admin2Lang: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      pendingDeactivation: null,
    }
  },
  computed: {
    statusLabel() {
      if (!this.enabled) {
        return 'Entwurf – nicht öffentlich'
      }
      if (!this.hasWebsite) {
        return 'Online auf mvp.de'
      }
      return this.marketed ? 'Online auf Ihrer Website & mvp.de' : 'Online auf Ihrer Website'
    },
    statusClass() {
      return this.enabled ? 'base-type-edit__status-badge--online' : 'base-type-edit__status-badge--draft'
    },
    statusActions() {
      if (!this.enabled) {
        return []
      }
      if (this.hasWebsite && this.marketed) {
        return [
          {action: 'stop-marketing', label: 'Von mvp.de entfernen'},
          {action: 'deactivate', label: 'Komplett offline nehmen'},
        ]
      }
      return [{action: 'deactivate', label: 'Offline nehmen'}]
    },
    displayName() {
      const name = String(this.objectName || '').trim()
      return name ? `„${name}“` : 'Der Eintrag'
    },
    deactivationText() {
      if (this.pendingDeactivation === 'stop-marketing') {
        return {
          header: 'Von mvp.de entfernen?',
          body: `${this.displayName} wird nicht mehr auf mvp.de angezeigt. Auf Ihrer Website bleibt der Eintrag online, und der Platz in Ihrem mvp.de-Kontingent wird wieder frei.`,
          confirm: 'Von mvp.de entfernen',
        }
      }
      if (this.hasWebsite && this.marketed) {
        return {
          header: 'Komplett offline nehmen?',
          body: `${this.displayName} ist danach weder auf Ihrer Website noch auf mvp.de zu sehen.`,
          confirm: 'Komplett offline nehmen',
        }
      }
      return {
        header: 'Offline nehmen?',
        body: this.hasWebsite
          ? `${this.displayName} ist danach nicht mehr auf Ihrer Website zu sehen.`
          : `${this.displayName} ist danach nicht mehr auf mvp.de zu sehen, und der Platz in Ihrem Kontingent wird wieder frei.`,
        confirm: 'Offline nehmen',
      }
    },
    showValidationAlert() {
      return Object.keys(this.validationErrors).length > 0
    },
    validationErrors() {
      return {...this.errors, ...this.publicationErrors}
    },
    premiumPreviewUrl() {
      return appendPremiumPreviewParam(this.previewUrl)
    },
    admin2Url() {
      const hasAdmin2Access = this.userStore.user?.isStaffMember()
        || this.userStore.isImpersonating

      if (!hasAdmin2Access || !this.admin2ObjectId) {
        return null
      }

      return buildAdmin2ObjectLocalizationEditUrl(this.admin2ObjectId, this.admin2Lang || 'de')
    },
  },
  methods: {
    confirmDeactivation() {
      const action = this.pendingDeactivation
      this.pendingDeactivation = null
      this.$emit('publication-action', action)
    },
    confirmMarketing() {
      this.$emit('update:marketingPrompt', null)
      this.$emit('publication-action', 'activate-marketing')
    },
  },
}
</script>
